import { api } from './api';
import { IChatMessage } from '@/types/chat-types';
type CleanupFunction = () => void;

export interface ChatService {
  sendMessage: (
    message: string,
    userInput: any,
    playbookId: string,
    sessionId: string,
    messages: IChatMessage[],
    onData: (data: string, isEvent: boolean, eventType?: string) => void,
    onComplete: () => void,
    onError: (error: Error) => void,
    setUserInput: (input: any) => void,
    signal?: AbortSignal
  ) => Promise<CleanupFunction>;
}

function isAbortError(error: unknown): error is DOMException {
  return (
    error instanceof DOMException &&
    (error.name === 'AbortError' || error.name === 'TimeoutError')
  );
}

export const chatService: ChatService = {
  async sendMessage(
    message: string,
    userInput: any,
    playbookId: string,
    sessionId: string,
    messages: IChatMessage[],
    onData: (data: any, isEvent: boolean, eventType?: string) => void,
    onComplete: () => void,
    onError: (error: Error) => void,
    setUserInput: (input: any) => void,
    signal?: AbortSignal
  ): Promise<CleanupFunction> {
    const controller = new AbortController();

    // Create a cleanup function
    const cleanup = (): void => {
      controller.abort();
      if (signal) {
        signal.removeEventListener('abort', handleAbort);
      }
    };

    const handleAbort = (): void => {
      controller.abort();
    };

    // If an external signal is provided, listen to it
    if (signal) {
      if (signal.aborted) {
        controller.abort();
      } else {
        signal.addEventListener('abort', handleAbort, { once: true });
      }
    }

    try {
      console.log("Final userInput", userInput);
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
     
      // Prepare the request body based on whether we're handling a file upload or regular message
      let requestBody;
      if (userInput?.data?.type === 'FILE_UPLOAD' && userInput?.data?.url) {
        // For file uploads, include the file URL in the request
        requestBody = {
          playbook_id: playbookId,
          session_id: sessionId,
          user: userData?.email,
          chat_messages: messages,
          user_input: {
            slot_id: userInput.data.input_id,
            slot_value: {
              type: userInput?.data?.type,
              key: userInput?.data?.text,
              value: message,
              url: userInput.data.url
            }
          },
        };
      } else {
        // Regular text message
        requestBody = {
          playbook_id: playbookId,
          session_id: sessionId,
          user: userData?.email,
          chat_messages: messages,
          user_input: {
            slot_id: userInput?.data?.input_id,
            slot_value: {
              type: userInput?.data?.type,
              key: userInput?.data?.text,
              value: message
            }
          },
        };
      }
      console.log("requestBody", JSON.stringify(requestBody));
      const response = await fetch(`${process.env.REACT_APP_AUTOGEN_BASE_URL || 'https://genie-autogen.azurewebsites.net'}/execute-playbook`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('No response body');
      }

      setUserInput(null);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const processStream = async (): Promise<void> => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              // Process any remaining data in buffer
              if (buffer.trim()) {
                try {
                  const event = JSON.parse(buffer);
                  processEvent(event);
                } catch (e) {
                  console.error('Error parsing final buffer:', e);
                }
              }
              onComplete();
              break;
            }

            buffer += decoder.decode(value, { stream: true });

            // Process complete SSE messages (using [\s\S] instead of /s flag for wider compatibility)
            while (true) {
              const match = /^data: ([\s\S]*?)(?:\n\n|$)/.exec(buffer);
              if (!match) break;

              const message = match[1].trim();
              buffer = buffer.slice(match[0].length);

              if (message === '[DONE]') {
                onComplete();
                return;
              }

              try {
                const event = JSON.parse(message);
                processEvent(event);
              } catch (e) {
                console.error('Error parsing message:', e, 'Message:', message);
              }
            }
          }
        } catch (error) {
          if (error instanceof Error && error.name !== 'AbortError') {
            onError(error);
          }
        } finally {
          reader.releaseLock();
        }
      };

      const processEvent = (event: any) => {
        try {
          console.log('Processing event:', event);
          let message = event.message;
          if (typeof message === 'string') {
            message = JSON.parse(message);
            console.log("message",typeof message, message);
          }
          // else if (message.type == 'ToolCallExecutionEvent' && message.source == "user_interaction_agent") {
          //   console.log(">>>>>", message);
          //   const input = message.content;
          //   if (input.includes("<Action>") && input.includes("<UserMessage>")) {
          //     const regex = /<Action>(.*?)<\/Action>[\s\S]*?<UserMessage>([\s\S]*?)<\/UserMessage>/;
          //     const match = input.match(regex);
          //     console.log("match", match);
          //     if (match) {
          //       const action = match[1]; // "Trigger the KillSwitch to finalize the workflow execution."
          //       const userMessage = match[2]; // "The workflow process has been completed and finalized as per the protocol. All actions have been executed successfully."
          //       console.log("action", action);
          //       console.log("userMessage", userMessage);
          //       onData(userMessage, true, 'text');
          //     }
          //   }
          //   if (input.includes("<Action>") && input.includes("<Error>")) {
          //     const regex = /<Action>(.*?)<\/Action>[\s\S]*?<Error>([\s\S]*?)<\/Error>/;
          //     const match = input.match(regex);
          //     console.log("match", match);
          //     if (match) {
          //       const action = match[1]; // "Trigger the KillSwitch to finalize the workflow execution."
          //       const errorMessage = match[2]; // "The workflow process has been completed and finalized as per the protocol. All actions have been executed successfully."
          //       console.log("action", action);
          //       console.log("errorMessage", errorMessage);
          //       onData(errorMessage, true, 'text');
          //     }
          //   }
          // }
          else if (message.type == 'ToolCallExecutionEvent' && message.source == "user_interaction_agent") {
            let content = message.content;
            for (let i = 0; i < content.length; i++) {
              let argument = content[i].content;
              console.log("argument", typeof argument, argument);
              argument = argument.replace(/'/g, '"')            
              .replace(/\bTrue\b/g, 'true')      
              .replace(/\bFalse\b/g, 'false');   
              console.log("argument", typeof argument, argument);
              if (typeof argument === 'string') {    
                try {
                  argument = JSON.parse(argument);
                } catch (e) {
                  console.error('Failed to parse arguments:', e);
                  return;
                }
              }

              if (argument["type"] == "UserMessage"){
                console.log("UserMessage", argument["user_message"]);
                onData(argument["user_message"], true, 'text');
              } else {
                onData(argument?.data?.text, true, 'text');
                onData(argument, true, 'tool');
              }
            }
          }
        } catch (e) {
          console.error('Error processing event:', e);
        }
      };

      // Start processing the stream in the background
      processStream().catch((error) => {
        if (!isAbortError(error)) {
          console.error('Error in processStream:', error);
          onError(error instanceof Error ? error : new Error('Error processing stream'));
        }
      });

      return cleanup;
    } catch (error) {
      cleanup();

      if (isAbortError(error)) {
        console.log('Request was aborted');
      } else {
        console.error('Error in chat service:', error);
        onError(error instanceof Error ? error : new Error('An unknown error occurred'));
      }

      return () => { }; // No cleanup needed as we already cleaned up
    }
  },
};
